export const CHANNEL_NAME = 'weave-open-tabs';

type Message = { type: 'announce'; tabId: string } | { type: 'leave'; tabId: string } | { type: 'who' };

const peers = new Set<string>();
let activeTabId: string | null = null;
let activeChannel: BroadcastChannel | null = null;

export function getOpenTabCount(): number {
  if (typeof BroadcastChannel === 'undefined') return 1;
  return peers.size;
}

// Test-only reset for module-level state between specs.
export function resetOpenTabCountForTest(): void {
  activeChannel?.close();
  peers.clear();
  activeTabId = null;
  activeChannel = null;
}

/**
 * Announces this tab on a BroadcastChannel and listens for peers.
 * Returns a cleanup function (also fires automatically on pagehide).
 */
export function registerOpenTab(tabId: string): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  // Idempotent if the same tab re-registers (e.g. StrictMode double-invoke)
  if (activeTabId === tabId && activeChannel) return () => {};

  activeTabId = tabId;
  peers.add(tabId);

  const channel = new BroadcastChannel(CHANNEL_NAME);
  activeChannel = channel;

  const handleMessage = (event: MessageEvent<Message>) => {
    const msg = event.data;
    if (!msg) return;

    switch (msg.type) {
      case 'announce':
        peers.add(msg.tabId);
        break;
      case 'leave':
        peers.delete(msg.tabId);
        break;
      case 'who':
        if (activeTabId) {
          safePostMessage(channel, { type: 'announce', tabId: activeTabId });
        }
        break;
    }
  };

  channel.addEventListener('message', handleMessage);

  const announce = () => {
    peers.add(tabId);
    safePostMessage(channel, { type: 'announce', tabId });
  };

  announce();

  // Ask everyone else to announce themselves so we know who is out there
  safePostMessage(channel, { type: 'who' });

  let isRegistered = true;
  const unregister = () => {
    if (!isRegistered) return;
    isRegistered = false;

    safePostMessage(channel, { type: 'leave', tabId });
    channel.removeEventListener('message', handleMessage);
    channel.close();
    peers.delete(tabId);
    if (activeChannel === channel) activeChannel = null;
    if (activeTabId === tabId) activeTabId = null;
    window.removeEventListener('pagehide', unregister);
  };

  // when the page is unloaded, we need to make sure to remove the tab from the count and notify peers
  window.addEventListener('pagehide', unregister);
  return unregister;
}

function safePostMessage(channel: BroadcastChannel, message: Message) {
  try {
    // 2 possible errors here: channel could be closed, or the postMessage itself could fail (e.g. if the message is too large). We catch both to avoid unhandled exceptions.
    channel.postMessage(message);
  } catch {
    console.error('Failed to post message on BroadcastChannel', message);
  }
}
